import { scormApi } from "@/getinfo-api/index";

export type CMIElement = string;
export type CMIErrorCode = string;

export class Scorm12 {
  private api: ScormApi;

  constructor(api: ScormApi) {
    this.api = api;
  }

  LMSInitialize(msg: string): boolean {
    return this.api.Initialize(msg);
  }

  LMSFinish(msg: string): boolean {
    return this.api.Terminate(msg);
  }

  LMSGetValue(element: CMIElement): string {
    return this.api.GetValue(element);
  }

  LMSSetValue(element: CMIElement, value: string): string {
    return this.api.SetValue(element, value);
  }

  LMSCommit(msg: string): boolean {
    return this.api.Commit(msg);
  }

  LMSGetLastError(): string {
    return this.api.GetLastError();
  }

  LMSGetErrorString(errorCode: CMIErrorCode): string {
    return this.api.GetErrorString(errorCode);
  }

  LMSGetDiagnostic(errorCode: CMIErrorCode): string {
    return this.api.GetDiagnostic(errorCode);
  }
}

class Scorm2004 {
  private api: ScormApi;

  constructor(api: ScormApi) {
    this.api = api;
  }

  Initialize(msg: string): boolean {
    return this.api.Initialize(msg);
  }

  Terminate(msg: string): boolean {
    return this.api.Terminate(msg);
  }

  GetValue(element: CMIElement): string {
    return this.api.GetValue(element);
  }

  SetValue(element: CMIElement, value: string): string {
    return this.api.SetValue(element, value);
  }

  Commit(msg: string): boolean {
    return this.api.Commit(msg);
  }

  GetLastError(): string {
    return this.api.GetLastError();
  }

  GetErrorString(errorCode: CMIErrorCode): string {
    return this.api.GetErrorString(errorCode);
  }

  GetDiagnostic(errorCode: CMIErrorCode): string {
    return this.api.GetDiagnostic(errorCode);
  }
}

interface ScormApi {
  Initialize(msg: string): boolean;
  Terminate(msg: string): boolean;
  GetValue(element: CMIElement): string;
  SetValue(element: CMIElement, value: string): string;
  Commit(msg: string): boolean;
  GetLastError(): CMIErrorCode;
  GetErrorString(errorCode: CMIErrorCode): string;
  GetDiagnostic(errorCode: CMIErrorCode): string;
}

const SCORM_ERRORS: { [key: string]: string } = {
  "0": "Unknown error",
  "101": "LMS already initialized",
  "102": "LMS terminated",
  "103": "LMS already terminated",
  "104": "LMS not initialized"
};

class ScormApiClass implements ScormApi {
  private initialized: boolean = false;
  private terminated: boolean = false;
  private values: { [key: string]: string } = {};

  private errorCode?: CMIErrorCode;

  private sessionId: string;
  private learningUnitId: string;
  private courseId: string;
  private callBack: Function;

  constructor(
    courseId: string,
    sessionId: string,
    learningUnitId: string,
    callBack: Function
  ) {
    this.courseId = courseId;
    this.sessionId = sessionId;
    this.learningUnitId = learningUnitId;
    this.callBack = callBack;
  }

  Initialize(): boolean {
    if (this.initialized) {
      this.throwError("101");
      return false;
    } else if (this.terminated) {
      this.throwError("102");
      return false;
    }

    this.initialized = true;

    return true;
  }

  GetValue(element: CMIElement): string {
    return this.values[element] ?? "";
  }

  SetValue(element: CMIElement, value: string): string {
    return (this.values[element] = value.toString());
  }

  Commit(): boolean {
    scormApi.ScormLearningProgress.commitScorm(
      this.courseId,
      this.learningUnitId,
      {
        sessionId: this.sessionId,
        values: this.values
      }
    ).then(() => {
      this.callBack();
    });
    return true;
  }

  Terminate(): boolean {
    if (this.terminated) {
      this.throwError("103");
      return false;
    } else if (!this.initialized) {
      this.throwError("104");
      return false;
    }
    scormApi.ScormLearningProgress.finishScorm(
      this.courseId,
      this.learningUnitId,
      {
        sessionId: this.sessionId
      }
    );

    this.terminated = true;

    return true;
  }

  GetLastError(): CMIErrorCode {
    return this.errorCode ?? "0";
  }

  GetErrorString(errorCode: CMIErrorCode): string {
    return SCORM_ERRORS[errorCode] ?? "Unknown error";
  }

  GetDiagnostic(errorCode: CMIErrorCode): string {
    return this.GetErrorString(errorCode);
  }

  private throwError(errorCode: CMIErrorCode): void {
    this.errorCode = errorCode;

    console.error(
      `Scorm Error ${this.errorCode} - ${this.GetErrorString(this.errorCode)}`
    );
  }
}

class LoggedScormApi implements ScormApi {
  private inner: ScormApi;

  constructor(inner: ScormApi) {
    this.inner = inner;
  }

  Initialize(msg: string): boolean {
    console.info("Scorm LMS Initialize");

    return this.inner.Initialize(msg);
  }

  GetValue(element: CMIElement): string {
    console.info(`Scorm LMS GetValue(${element})`);

    return this.inner.GetValue(element);
  }

  SetValue(element: CMIElement, value: string): string {
    console.info(`Scorm LMS SetValue(${element}, ${value})`);

    return this.inner.SetValue(element, value);
  }

  Commit(msg: string): boolean {
    console.info(`Scorm LMS Commit()`);

    return this.inner.Commit(msg);
  }

  Terminate(msg: string): boolean {
    console.info(`Scorm LMS Terminate()`);
    //@ts-ignore
    console.info(this.inner.values);

    return this.inner.Terminate(msg);
  }

  GetLastError(): CMIErrorCode {
    return this.inner.GetLastError();
  }

  GetErrorString(errorCode: CMIErrorCode): string {
    return this.inner.GetErrorString(errorCode);
  }

  GetDiagnostic(errorCode: CMIErrorCode): string {
    return this.inner.GetDiagnostic(errorCode);
  }
}

export const createScorm2004 = (
  courseId: string,
  sessionId: string,
  learningUnitId: string,
  loggable: boolean = true,
  callBack: any
) => {
  return loggable
    ? new Scorm2004(
        new LoggedScormApi(
          new ScormApiClass(courseId, sessionId, learningUnitId, callBack)
        )
      )
    : new Scorm2004(
        new ScormApiClass(courseId, sessionId, learningUnitId, callBack)
      );
};

export const createScorm12 = (
  courseId: string,
  sessionId: string,
  learningUnitId: string,
  loggable: boolean = true,
  callBack: any
) => {
  return loggable
    ? new Scorm12(
        new LoggedScormApi(
          new ScormApiClass(courseId, sessionId, learningUnitId, callBack)
        )
      )
    : new Scorm12(
        new ScormApiClass(courseId, sessionId, learningUnitId, callBack)
      );
};

//
// class ScormApiBuilder {
//   private credentials?: {
//     courseId: string;
//     sessionId: string;
//     learningUnitId: string;
//   };
//
//   private loggable: boolean = false;
//
//   public setCredentials(
//     courseId: string,
//     sessionId: string,
//     learningUnitId: string
//   ): ScormApiBuilder {
//     this.credentials = { courseId, sessionId, learningUnitId };
//
//     return this;
//   }
//
//   public setLoggable(loggable: boolean): ScormApiBuilder {
//     this.loggable = loggable;
//
//     return this;
//   }
//
//   public buildScorm12(): Scorm12 {
//     return new Scorm12(this.build());
//   }
//
//   private build(): ScormApi {
//     let api: ScormApi;
//
//     if (this.credentials) {
//       api = new ScormApiClass(
//         this.credentials.courseId,
//         this.credentials.sessionId,
//         this.credentials.learningUnitId
//       );
//     } else {
//       api = new FakeScormApi(); // todo Create FakeScormApi
//     }
//
//     if (this.loggable) {
//       api = new LoggedScormApi(api);
//     }
//
//     return api;
//   }
// }
//
// // todo test
// const builder = new ScormApiBuilder();
// builder.setLoggable(true);
// builder.buildScorm12();
